import { OrthographicCamera, Vector2, Vector3 } from 'three';
import {
    calculate_halo_difference,
    find_intersection_object,
    get_horizontal_vertical_rotate_direction,
    move_camera_by_vector,
    raycaster_intersect_objects,
    rotate_camera_by_axis_and_angle,
    set_horizontal_vertical_rotate_direction,
    suppress_undo_tracking,
} from '@frontend/utility';
import { pointer_normalised,
    pointer_normalised_prev,
} from '@frontend/pointer';
import { uil_deflate_face, uil_drag_edge, uil_inflate_face } from '@frontend/ui_logic';
import {
    animate,
    attempt_to_attach_working_plane_to_face_for_dragging,
    hide_edge_midpoint,
    hide_halo,
    zoom_in_or_out,
} from '@frontend/scene';
import { from_2d_canvas_coordinates_to_3d_world_coordinates, plane_ray_intersect } from '@frontend/geometry_and_math';
import { clipping_planes, show_clipping_planes } from '@features/clipping';
import {
    rigid_move_manipulator_cube,
    rigid_move_manipulator_plane,
    rigid_move_manipulator_ring,
    rigid_move_manipulator_rod,
    rigid_move_manipulator_tip,
} from './rigid-move-manipulator';
import { is_nan } from '@phi/maths/vector2';
import App from '../../application-container';


/** Rotates the camera based on a cursor movement, keeping the rotation target at the same location within the viewport. */
export function rotate_camera_between_two_cursor_locations(cam: OrthographicCamera, rotation_target: Vector3, prev: Vector2, curr: Vector2) {
    if (is_nan(prev) || is_nan(curr))
        return;
    if (!rotation_target)
        rotation_target = new Vector3();

    const diff = new Vector2().subVectors(prev, curr);
    const angle = diff.length() * 10.0; // Should make this configurable.
    const eyeDirection = new Vector3().subVectors(cam.position, rotation_target);
    const upDirection = new Vector3().copy(cam.up);
    const sidewaysDirection = new Vector3().crossVectors(upDirection, eyeDirection);
    upDirection.setLength(curr.y - prev.y);
    sidewaysDirection.setLength(curr.x - prev.x);
    const moveDirection = new Vector3().copy(upDirection.add(sidewaysDirection));
    const axis = new Vector3().crossVectors(moveDirection, eyeDirection).normalize();
    rotate_camera_by_axis_and_angle(cam, rotation_target, axis, angle);
}

export function rotate() {

    set_horizontal_vertical_rotate_direction(pointer_normalised, pointer_normalised_prev);

    const pointer_limited = new Vector2().copy(pointer_normalised);
    const pointerprev_limited = new Vector2().copy(pointer_normalised_prev);

    //console.log('App.state.rotation_locked: ', App.state.rotation_locked);
    //console.log('get_horizontal_vertical_rotate_direction(): ', get_horizontal_vertical_rotate_direction());

    if (App.state.rotation_locked && get_horizontal_vertical_rotate_direction() == 'h') {
        pointer_limited.y = 0;
        pointerprev_limited.y = 0;
    } else if (App.state.rotation_locked && get_horizontal_vertical_rotate_direction() == 'v') {
        pointer_limited.x = 0;
        pointerprev_limited.x = 0;
    }

    rotate_camera_between_two_cursor_locations(
        uid_camera,
        uid_state.camera_target_point!,
        pointerprev_limited,
        pointer_limited,
    );
    animate();
}

export function drag_face(mousenormalized: Vector2) {
    const o = new Vector3();
    const n = new Vector3();

    // Perhorm elevator drag to inflate/deflate face

    let halo_difference = 0;
    let halo_difference_prev = 0;

    if (drag_start_movement_direction !== 'free') {
        // If this is the first shift step, then mark in which direction we're going., x, y, or z.
        // Carefull to distinguish between the actual ray origin and the vertex position.
        if (drag_start_movement_direction == 'none') {
            drag_start_movement_direction = 'halo';
            // Sto tracking undo
            suppress_undo_tracking();
        }

        // Here we perform a completely different kind of drag where we measure how far (in 2D screen mousecoordinates)
        // the mouse has moved from the location of the drag start, and we interpret this as a drag a long the uid_halo_direction axis. We have to construct this
        // mechanism from scratch, and calculate an origin o and normal n for the drag ray.

        // Determine how far the mouse has moved
        if (uid_halo) {
            halo_difference = calculate_halo_difference(mousenormalized, uid_halo.normal, uid_halo_initial_horizontal_2D_position, uid_halo_initial_vertical_2D_position);
            halo_difference_prev = calculate_halo_difference(pointer_normalised_prev, uid_halo.normal, uid_halo_initial_horizontal_2D_position, uid_halo_initial_vertical_2D_position);
        }

        // Move along the axis by this distance to find our drag origin o.
        o.copy(uid_halo.normal);
        o.multiplyScalar(halo_difference);
        o.add(uid_halo.center);

        // Find a normal to this axis, any will do really. We cross a random vector with ui_halo_direction to get a normal to the two.
        n.x = 0.316227766;
        n.y = 0.948683298;
        n.z = 0.572665981;
        n.cross(uid_halo.normal).normalize();
    }

    if (halo_difference > halo_difference_prev) {
        if (uid_selected_faces.length == 0)
            uil_inflate_face(uid_drag_object.id);
        else
            for (const fid of uid_selected_faces.keys())
                uil_inflate_face(fid);
    } else if (halo_difference < halo_difference_prev) {
        if (uid_selected_faces.length == 0)
            uil_deflate_face(uid_drag_object.id);
        else
            for (const fid of uid_selected_faces.keys())
                uil_deflate_face(fid);
    }

    hide_halo();

    // Store raycaster origin to use in next drag step.
    uid_dragging_raycaster_previous_origin.copy(o);
    return 1;
}

export function pan() {
    const v = new Vector3().subVectors(
        from_2d_canvas_coordinates_to_3d_world_coordinates(pointer_normalised_prev),
        from_2d_canvas_coordinates_to_3d_world_coordinates(pointer_normalised),
    );
    move_camera_by_vector(uid_camera, v);
    animate();
}

export function zoom(amount: number) {
    return zoom_in_or_out(amount);
}

export function find_intersection() {
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    // Save location of camera on click
    uid_camera_click_origin.copy(uid_raycaster.ray.origin);
    uid_camera_click_direction.copy(uid_raycaster.ray.direction);
    uid_camera_click_up.copy(uid_camera.up);

    const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

    return find_intersection_object(intersects);
}

export function mark_click_location_on_edge(x: Vector3) {
    uid_selected_edge_click_point = new Vector3();
    uid_selected_edge_click_point.copy(x);
}

export function handle_mousemove_while_clickdragging_edge(mousenormalized: Vector2, elevator_drag: boolean) {
    let o = new Vector3();
    const n = new Vector3();

    hide_edge_midpoint(true, true);

    if (uid_state.in_first_drag_step) {
        // Before starting the drag see if we can attach a working plane
        attempt_to_attach_working_plane_to_face_for_dragging('edge', uid_drag_object.id);
    }

    if (elevator_drag && drag_start_movement_direction != 'free') {
        // If this is the first shift step, then mark in which direction we're going., x, y, or z.
        // Carefull to distinguish between the actual ray origin and the vertex position.
        if (drag_start_movement_direction == 'none')
            drag_start_movement_direction = 'halo';

        // Here we perform a completely different kind of drag that I call 'elevator drag'. Basically we measure how far up or down (in 2D screen mousecoordinates)
        // the mouse has moved from the location of the drag start, and we interpret this as a drag a long the uid_halo_direction axis. We have to construct this
        // mechanism from scratch, and calculate an origin o and normal n for the drag ray.

        // Determine how far up or down the mouse has moved
        let halo_difference = 0;
        if (uid_halo)
            halo_difference = calculate_halo_difference(mousenormalized, uid_halo.normal, uid_halo_initial_horizontal_2D_position, uid_halo_initial_vertical_2D_position);

        // Project raycaster point onto halo axis
        const v1 = new Vector3();
        v1.copy(uid_raycaster.ray.direction);
        v1.cross(uid_halo.normal);
        v1.cross(uid_raycaster.ray.direction);
        o = plane_ray_intersect(uid_raycaster.ray.origin, v1, uid_halo.center, uid_halo.normal);

        // Find a normal to this axis, any will do really. We cross a random vector with ui_halo_direction to get a normal to the two.
        n.x = 0.316227766;
        n.y = 0.948683298;
        n.z = 0.572665981;
        n.cross(uid_halo.normal).normalize();

        // Also show the halo axis pointing towards the drag direction
        uid_halo.extend_axis(halo_difference > 0);
    } else if (working_plane_set){
        // We'll be moving the vertex within a plane parallel to the working plane that passes through the vertex initial position.
        const b1 = plane_ray_intersect(last_intersection_coordinates, working_plane_normal, uid_raycaster.ray.origin, uid_raycaster.ray.direction, 1);
        o.copy(b1);
        n.copy(working_plane_normal);
        hide_halo();
    } else {
        o.copy(uid_raycaster.ray.origin);
        n.copy(uid_raycaster.ray.direction);
        hide_halo();
    }

    uil_drag_edge(uid_drag_object.id, o, n);
    // Store raycaster origin to use in next drag step.
    uid_dragging_raycaster_previous_origin.copy(o);

    return 1;
}

export function drag_clipping_plane() {
    // Clipping plane is displayed.
    // First, check that we have pressed the mouse over some clipping plane
    if (uid_state.clipping_plane_selected === '')
        return;

    // Calculate mouse movement along x, y and z direction
    const v = new Vector3().subVectors(
        from_2d_canvas_coordinates_to_3d_world_coordinates(pointer_normalised_prev),
        from_2d_canvas_coordinates_to_3d_world_coordinates(pointer_normalised),
    );

    const move_vector = new Vector3();
    if (uid_state.clipping_plane_selected == 'xymax_clipping_plane' || uid_state.clipping_plane_selected == 'xymin_clipping_plane')
        move_vector.set(0, 0, 1);
    else if (uid_state.clipping_plane_selected == 'yzmax_clipping_plane' || uid_state.clipping_plane_selected == 'yzmin_clipping_plane')
        move_vector.set(1, 0, 0);
    else
        move_vector.set(0, 1, 0);

    // Project movement vector v on move_vector
    const dist = v.dot(move_vector);

    // Apply movement to selected plane
    const collision_threshold = uid_global_bb.dimension / 100.0;

    if (uid_state.clipping_plane_selected == 'xymax_clipping_plane') {
        clipping_planes.xy.max -= dist;
        if (clipping_planes.xy.max <= clipping_planes.xy.min + collision_threshold)
            clipping_planes.xy.max = clipping_planes.xy.min + collision_threshold;
    } else if (uid_state.clipping_plane_selected == 'yzmax_clipping_plane') {
        clipping_planes.yz.max -= dist;
        if (clipping_planes.yz.max <= clipping_planes.yz.min + collision_threshold)
            clipping_planes.yz.max = clipping_planes.yz.min + collision_threshold;
    } else if (uid_state.clipping_plane_selected == 'xzmax_clipping_plane') {
        clipping_planes.xz.max -= dist;
        if (clipping_planes.xz.max <= clipping_planes.xz.min + collision_threshold)
            clipping_planes.xz.max = clipping_planes.xz.min + collision_threshold;
    } else if (uid_state.clipping_plane_selected == 'xymin_clipping_plane') {
        clipping_planes.xy.min -= dist;
        if (clipping_planes.xy.min >= clipping_planes.xy.max - collision_threshold)
            clipping_planes.xy.min = clipping_planes.xy.max - collision_threshold;
    } else if (uid_state.clipping_plane_selected == 'yzmin_clipping_plane') {
        clipping_planes.yz.min -= dist;
        if (clipping_planes.yz.min >= clipping_planes.yz.max - collision_threshold)
            clipping_planes.yz.min = clipping_planes.yz.max - collision_threshold;
    } else if (uid_state.clipping_plane_selected == 'xzmin_clipping_plane') {
        clipping_planes.xz.min -= dist;
        if (clipping_planes.xz.min >= clipping_planes.xz.max - collision_threshold)
            clipping_planes.xz.min = clipping_planes.xz.max - collision_threshold;
    }

    show_clipping_planes();
}

export function handle_mousemove_while_clickdragging_manipulator_element(modifier: boolean) {
    // The user is click-dragging a manipulator element.

    // First we reset the pop_amount_value as any movement will no longer allow us to apply it.
    pop_amount_value = 0;

    // Reset the flag that the manipulator was just shown after a body copy
    uid_state.rigid_move_manipulator_shown_after_copy_body = false;

    if (uid_rigid_move_manipulator)
        uid_rigid_move_manipulator.set_has_operated();

    // Keep a record of the previous time we raycasted.
    uid_manip_previous_raycaster_origin.copy(uid_manip_current_raycaster_origin);
    uid_manip_previous_raycaster_direction.copy(uid_manip_current_raycaster_direction);
    uid_manip_current_raycaster_origin.copy(uid_raycaster.ray.origin);
    uid_manip_current_raycaster_direction.copy(uid_raycaster.ray.direction);

    switch (uid_selected_manipulator_type) {
        case 'rigid_move_manip_cube':
            rigid_move_manipulator_cube(modifier);
            break;

        case 'rigid_move_manip_ringx':
        case 'rigid_move_manip_ringy':
        case 'rigid_move_manip_ringz':
        case 'rigid_move_manip_ringxTrans':
        case 'rigid_move_manip_ringyTrans':
        case 'rigid_move_manip_ringzTrans':
            rigid_move_manipulator_ring(modifier);
            break;

        case 'rigid_move_manip_vectorx_rod':
        case 'rigid_move_manip_vectory_rod':
        case 'rigid_move_manip_vectorz_rod':
        case 'rigid_move_manip_vectorx_rodTrans':
        case 'rigid_move_manip_vectory_rodTrans':
        case 'rigid_move_manip_vectorz_rodTrans':
        case 'rigid_move_manip_fence_tube':
            rigid_move_manipulator_rod(modifier);
            break;

        case 'rigid_move_manip_planexy':
        case 'rigid_move_manip_planeyz':
        case 'rigid_move_manip_planexz':
            rigid_move_manipulator_plane(modifier);
            break;

        case 'rigid_move_manip_vectorx_tip':
        case 'rigid_move_manip_vectory_tip':
        case 'rigid_move_manip_vectorz_tip':
        case 'rigid_move_manip_vectorx_tipTrans':
        case 'rigid_move_manip_vectory_tipTrans':
        case 'rigid_move_manip_vectorz_tipTrans':
            rigid_move_manipulator_tip(modifier);
            break;

        default: // Error
            break;
    }
}
